import { useQuery } from 'react-query';
import React, { ReactElement, useRef, useState } from 'react';

import { AccountNavigationMenu } from '@pb/shared/ui/molecules/account-navigation-menu';
import { AccountNavBar } from '@pb/shared/ui/molecules/account-nav-bar';
import { SkipToContent } from '@pb/shared/ui/atoms/skip-to-content';

import {
    getFormattedNotifications,
    TNormalisedNotification,
    useAuthorisationService,
} from '@pb/services';
import { useClickOutside, useIsDevice } from '@pb/hooks';
import {
    getCustomerNavigationMenuItems,
    getLPPNavigationMenuItems,
} from './account.header.links';
import { TAccountHeaderProperties } from './account-header.types';
import { StyledHeader } from './account-header.styled';

export function AccountHeader({
    navigationMenuItems,
    baseUrl,
    apiBaseUrl,
    isCustomer = true,
}: TAccountHeaderProperties): ReactElement {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);

    const accountNavigationReference = useRef<HTMLDivElement>(null);

    const authorisationService = useAuthorisationService(apiBaseUrl);

    useClickOutside(accountNavigationReference, onClickOutside);

    function onClickOutside(event: MouseEvent | TouchEvent) {
        event.stopPropagation();
        setIsAccountMenuOpen(false);
        setIsNotificationMenuOpen(false);
    }

    const { data } = useQuery(
        'loggedInUser',
        () => authorisationService.getLoggedInUser(),
        { refetchOnWindowFocus: false }
    );

    const { data: notificationListItems } = useQuery<TNormalisedNotification[]>(
        'notifications',
        getFormattedNotifications,
        { refetchOnWindowFocus: false }
    );

    function onMenuClick(event: React.MouseEvent | TouchEvent) {
        event.preventDefault();
        setIsMenuOpen(previousState => !previousState);
    }

    function onAccountMenuClick(event: React.MouseEvent | TouchEvent) {
        event.preventDefault();
        setIsAccountMenuOpen(previousState => !previousState);
    }

    const isMobile = useIsDevice('mobile');
    const isTabletPortrait = useIsDevice('tabletPortrait');

    const hideAccountNavigation = (isMobile || isTabletPortrait) && !isMenuOpen;
    const getNavigationMenuItems = isCustomer
        ? getCustomerNavigationMenuItems
        : getLPPNavigationMenuItems;

    return (
        <StyledHeader>
            <SkipToContent text="Skip to main content" href="#main" />
            <AccountNavBar
                reference={accountNavigationReference}
                homepageUrl={`${baseUrl}/seller#!dashboard`}
                onMenuClick={onMenuClick}
                isMenuOpen={isMenuOpen}
                onAccountMenuClick={onAccountMenuClick}
                isAccountMenuOpen={isAccountMenuOpen}
                isNotificationMenuOpen={isNotificationMenuOpen}
                setIsNotificationMenuOpen={setIsNotificationMenuOpen}
                notificationListItems={notificationListItems}
                accountNavigationMenuItems={getNavigationMenuItems(
                    data,
                    onAccountMenuClick
                )}
                isCustomer={isCustomer}
            />
            {!hideAccountNavigation && (
                <AccountNavigationMenu
                    navigationMenuItems={navigationMenuItems}
                    isMenuOpen={isMenuOpen}
                />
            )}
        </StyledHeader>
    );
}
